/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { AxiosError, AxiosInstance } from 'axios';
import type { Action, CompletePurchaseData, ThunkAction } from '../types';
import actionConstant from '../types/action';
import azURL, { getATGDataWithoutCache, relPath } from '../config/serviceAPI';
import { checkoutConstants } from '../constants/checkout';
import { type QueryClient } from '@tanstack/react-query';
import { type HeaderData } from '@/features/header';
import { getHeaderDataKey } from '@/features/header/api/getHeader';
import { type OrderDetailsData } from '../types/index';
import { getAcesFlag } from '@/features/ymme/utils/acesFlags';
import { getOMSFlags } from '@/features/product/utils/getOMSFlags';
import { getAvailabilityInfoKey } from '@/features/orders/api/getAvailabilityInfo';
import { getGCPPriceFlags } from '@/features/product/utils/getGCPPriceFlags';
import { isServer } from '@/utils/isServer';

const getMiniCartData = (data: CompletePurchaseData) => {
  const dataLength = data?.orders ? data.orders.length : 0;
  const headerData = {
    corePrice: 0,
    itemCount: 0,
    orderSubTotal: 0,
    orderDetails: data,
  };

  if (dataLength > 0) {
    headerData.orderSubTotal = data.grandOrderPriceInfo.totalPrice!;
    headerData.corePrice = data.grandOrderPriceInfo.corePrice;
    data.orders.forEach((order) => {
      order.shipmentInfoList.forEach((shipmentInfo) => {
        shipmentInfo.lineItemList.forEach((item) => {
          headerData.itemCount += Number(item.itemQuantity);
        });
      });
    });
  }

  return headerData;
};

export const requestingOrderItem = (): Action => ({
  type: actionConstant.UPDATE_ORDERITEM_REQUESTING,
});

export const getOrderDetails = async (
  axios: AxiosInstance,
  hideInStorePurchase = false,
  queryClient?: QueryClient,
): Promise<OrderDetailsData> => {
  let orderDetailsUrl = azURL(relPath.orderDetailsV4);

  if (hideInStorePurchase) {
    orderDetailsUrl += checkoutConstants.nonInStoreOrderTypesParams;
  }

  // Add ACES headers specifically for order details API call on server-side
  if (queryClient && isServer) {
    const { acesEnabled } = getAcesFlag(queryClient);
    const { useOMSInventoryQohService } = getOMSFlags(queryClient);
    const { useGCPPriceService } = getGCPPriceFlags(queryClient);

    const headers: Record<string, string> = {
      'X-Aces-Enabled': `${acesEnabled}`,
      useInventoryQohService: `${!!useOMSInventoryQohService}`,
      'X-Price-Enabled': `${!!useGCPPriceService}`,
    };

    // Use axios directly with headers for server-side calls
    const { data } = await axios.get<OrderDetailsData>(orderDetailsUrl, { headers });
    return data;
  }

  const { data } = await getATGDataWithoutCache<OrderDetailsData>(axios, orderDetailsUrl);
  return data;
};

export const fetchOrderDetails =
  (hideInStorePurchase = false, queryClient: QueryClient, currentPath?: string): ThunkAction =>
  async (dispatch, getState, axios) => {
    dispatch({ type: actionConstant.ORDER_DETAILS_REQUESTING });
    try {
      const data = await getOrderDetails(axios, hideInStorePurchase, queryClient);

      dispatch({
        type: actionConstant.ORDER_DETAILS_SUCCESS,
        data,
      });

      queryClient.setQueryData<HeaderData>(
        getHeaderDataKey(),
        (oldData: HeaderData | undefined) => {
          if (!oldData) {
            return undefined;
          }

          return {
            ...oldData,
            miniCartMap: {
              ...oldData.miniCartMap,
              ...getMiniCartData(data),
            },
            miniCartSuccessNote: true,
          };
        },
      );

      // Invalidate availability data on cart/checkout pages to ensure fresh data
      const pathToCheck =
        currentPath || (typeof window !== 'undefined' ? window.location.pathname : '');

      if (pathToCheck) {
        const isCartOrCheckoutPage =
          pathToCheck.includes('/cart') || pathToCheck.includes('/checkout');

        if (isCartOrCheckoutPage) {
          void queryClient.invalidateQueries({
            queryKey: getAvailabilityInfoKey(),
          });
        }
      }
    } catch (err: unknown) {
      const error = err as AxiosError;
      dispatch({
        type: actionConstant.ORDER_DETAILS_FAILURE,
        err: error.message,
        errorLog: err,
      });

      // Since this function is specifically for order details, always invalidate cache on error
      // to prevent showing stale data from previous successful calls
      if (queryClient) {
        void queryClient.invalidateQueries({
          queryKey: getHeaderDataKey(),
          exact: true, // Only invalidate the exact header query, not dependent queries
        });
      }
    }
  };

export const clearOrderDetails = (): Action => ({ type: actionConstant.CLEAR_ORDER_DETAILS });

export const setCheckoutButton = (checkoutButtonState: boolean): Action => ({
  type: actionConstant.SET_CHECKOUT_BUTTON,
  setCheckoutButton: checkoutButtonState,
});

export const setOrderStatus = (orderStatus: string): Action => ({
  type: actionConstant.SET_ORDER_STATUS,
  orderStatus,
});
